import { AccountType, IAccount, isGtmhubAccountId } from "@gtmhub/core";
import { IProfile } from "@webapp/users/models/users.models";
import { IAppConfig } from "@gtmhub/env";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { CurrentUserService } from "@gtmhub/users";
import { ISurveysSettings } from "./models";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";

declare const delighted: Survey;
declare const delightedCes: Survey;
declare const delightedNewNavi: Survey;

interface Survey {
  survey(surveyConfig: Partial<SurveyConfig>): void;
}
interface SurveyConfig {
  createdAt: string;
  email: string;
  properties: {
    userId: string;
    tags?: string[];
    customerSuccessManaged: string;
    locale: string;
    event?: string;
  };
  minTimeOnPage: number;
  recurringPeriod?: number;
  onRespond(surveyResult: SurveyResult): void;
  onShow(): void;
}

interface SurveyResult {
  score: number;
}

export class SurveyService {
  private surveySettings: ISurveysSettings;
  private userProfile: IProfile;
  private userId: string;

  public static $inject = ["appConfig", "AnalyticsService", "CurrentUserService", "AccountResolverService"];

  constructor(
    private appConfig: IAppConfig,
    private analyticsService: AnalyticsService,
    private currentUserService: CurrentUserService,
    private accountResolverService: AccountResolverService
  ) {
    this.surveySettings = currentUserService.getUserSetting<ISurveysSettings>("surveys") || { shown: [] };
    this.userProfile = JSON.parse(localStorage.getItem("profile"));
    this.userId = localStorage.getItem("userId");
  }

  public execDelightedSurvey(): void {
    const account = this.accountResolverService.getAccountData();

    if (this.shouldBeSurveyed(account) && this.isProfileValid() && this.calcDaysDiff(this.userProfile) && this.isLoginsCountEnough()) {
      const customLocaleSuffix = "-x-gtmhub";
      const surveyConfig: Partial<SurveyConfig> = this.getDefaultSurveyConfig(customLocaleSuffix, account);
      surveyConfig.properties.tags = this.appConfig.delighted.tags;
      surveyConfig.onRespond = (surveyResult: SurveyResult) => this.trackResponse(surveyResult, "NPS", "");

      delighted.survey(surveyConfig);
    }
  }

  public execDelightedNewNaviSurvey(): void {
    const account = this.accountResolverService.getAccountData();
    const seenNewNaviCount = Number(localStorage.getItem("seenNewNaviCount"));
    const REQUIRED_COUNT = 3;

    if (this.shouldBeSurveyed(account) && this.isProfileValid() && seenNewNaviCount >= REQUIRED_COUNT) {
      const customLocaleSuffix = "-x-gtmhub-3";
      const surveyConfig: Partial<SurveyConfig> = this.getDefaultSurveyConfig(customLocaleSuffix, account);
      surveyConfig.properties.tags = this.appConfig.delightedNewNavi.tags;
      surveyConfig.onRespond = (surveyResult: SurveyResult) => this.trackResponse(surveyResult, "NEW_NAVI", "");

      delightedNewNavi.survey(surveyConfig);
    }
  }

  public execDelightedCESSurvey(eventName: string): void {
    const account = this.accountResolverService.getAccountData();

    if (this.shouldBeSurveyed(account) && this.isProfileValid() && this.surveyNotShow(eventName) && this.isLoginsCountEnough()) {
      const customLocaleSuffix = "-x-gtmhub-2";
      const recurringPeriod = 30 * 24 * 3600;
      const surveyConfig: Partial<SurveyConfig> = this.getDefaultSurveyConfig(customLocaleSuffix, account);
      surveyConfig.properties.event = eventName;
      surveyConfig.properties.tags = this.appConfig.delightedCES.tags;
      surveyConfig.recurringPeriod = recurringPeriod;
      surveyConfig.onRespond = (surveyResult: SurveyResult) => this.trackResponse(surveyResult, "CES", eventName);
      surveyConfig.onShow = () => this.markAsShown(eventName);

      delightedCes.survey(surveyConfig);
    }
  }

  private trackResponse(surveyResult: SurveyResult, surveyType: string, eventName: string) {
    const account = this.accountResolverService.getAccountData();
    this.analyticsService.track("Survey Answered", {
      score: surveyResult.score,
      cs_managed: account.csManaged,
      type: surveyType,
      trigger_event: eventName,
      logins_count: Number(localStorage.getItem("loginsCount")),
    });
  }

  private markAsShown(eventName: string) {
    this.surveySettings.shown.push(eventName);
    this.currentUserService.setUserSetting({ surveys: this.surveySettings });
  }

  private getDefaultSurveyConfig(customLocaleSuffix: string, account: IAccount) {
    const customerSuccessManaged = account.csManaged ? "Yes" : "No";

    const userLocale = this.getLocaleSetting(this.userProfile, customLocaleSuffix);
    return {
      createdAt: this.userProfile.created,
      email: this.userProfile.email,
      properties: {
        userId: this.userId,
        customerSuccessManaged,
        locale: userLocale,
      },
      minTimeOnPage: 15,
    };
  }

  /*
   * Delighted by default provides a check if the user has seen the survey, but if we decide to turn it of we still need to make this check using this method
   */
  private surveyNotShow(surveyName: string): boolean {
    return !this.surveySettings.shown.includes(surveyName);
  }

  private isProfileValid(): boolean {
    return !!this.userId && !!this.userProfile && !!this.userProfile.email;
  }
  private getLocaleSetting(userProfile: IProfile, customLocaleSuffix: string): string {
    const userLanguage = userProfile.language ? userProfile.language.toLowerCase() : "";

    // this is delighted custom locale for our customized comment's placeholder text
    // delighted support can add texts on their end (not possible via their API)
    let userLocale = "en" + customLocaleSuffix;
    switch (userLanguage) {
      case "german":
        userLocale = "de" + customLocaleSuffix;
        break;
      case "bulgarian":
        userLocale = "bg" + customLocaleSuffix;
        break;
      case "chinese":
        userLocale = "zh-CN" + customLocaleSuffix;
        break;
      case "spanish":
        userLocale = "es" + customLocaleSuffix;
        break;
      case "french":
        userLocale = "fr" + customLocaleSuffix;
        break;
      case "portugese":
        userLocale = "pt" + customLocaleSuffix;
        break;
      default:
        userLocale = "en" + customLocaleSuffix;
        break;
    }
    return userLocale;
  }

  private shouldBeSurveyed(account: IAccount): boolean {
    return account && account.type !== AccountType.TrialAccount && account.type !== AccountType.InternalAccount && !isGtmhubAccountId(account.id);
  }

  private calcDaysDiff(userProfile: IProfile): boolean {
    // we want to show the survey after 30 days from the user create
    const userDateCreated = new Date(userProfile.created).getTime();
    const todaysDate = new Date().getTime();
    const daysDiff = (todaysDate - userDateCreated) / (1000 * 3600 * 24);
    const daysToShowSurveyAfterUserCreated = 30;

    return daysDiff > daysToShowSurveyAfterUserCreated;
  }

  private isLoginsCountEnough(): boolean {
    const loginsCount = Number(localStorage.getItem("loginsCount"));
    const previousLoginsRequired = 4;

    if (loginsCount) {
      return loginsCount > previousLoginsRequired;
    }

    // returning true if for some reason loginsCount is not set to localStorage to show the survey
    return true;
  }
}
